import type {
  CampaignReportResponse,
  ComparisonReportResponse,
  HessReportResponse,
  LinearReportResponse,
  SocialCampaignReportResponse,
  StreamingReportResponse,
} from "../types";

export type ReportData =
  | CampaignReportResponse
  | ComparisonReportResponse
  | LinearReportResponse
  | StreamingReportResponse
  | SocialCampaignReportResponse
  | HessReportResponse;

export type ReportType = ReportData["report_type"];

export type ReportLayoutKind =
  | "gyoza"
  | "comparison"
  | "streaming"
  | "linear"
  | "social"
  | "hess"
  | "offsite"
  | "onsite"
  | "campaign-original"
  | "campaign-slide"
  | "campaign-compact";

export interface SavedReportSummaryFields {
  date_range_start: string;
  date_range_end: string;
  total_sales?: string;
  total_spend?: string;
  campaign_count?: number;
  brand_count?: number;
  channel_count?: number;
}

export interface ReportHistorySummary {
  primaryValue?: string;
  primaryLabel?: "sales" | "spend";
  detailLeft: string;
  detailRight: string;
}

export interface ReportFamilyAdapter<TData extends ReportData = ReportData> {
  label: string;
  colorClasses: string;
  layoutKind: ReportLayoutKind;
  getSavedFields: (data: TData) => SavedReportSummaryFields;
  getHistorySummary: (report: SavedReportSummaryFields & { data: TData }) => ReportHistorySummary;
}

const campaignFields = (
  data: CampaignReportResponse,
): SavedReportSummaryFields => ({
  date_range_start: data.summary.date_range_start,
  date_range_end: data.summary.date_range_end,
  total_sales: data.summary.total_sales,
  total_spend: data.summary.total_spend,
  campaign_count: data.summary.campaign_count,
  brand_count: data.summary.brand_count,
});

const campaignHistorySummary = (
  report: SavedReportSummaryFields & { data: CampaignReportResponse },
): ReportHistorySummary => ({
  primaryValue: report.total_sales,
  primaryLabel: "sales",
  detailLeft: `${report.campaign_count || 0} campaigns`,
  detailRight: `${report.brand_count || 0} brands`,
});

const campaignAdapter = (
  label: string,
  colorClasses: string,
  layoutKind: ReportLayoutKind,
): ReportFamilyAdapter<CampaignReportResponse> => ({
  label,
  colorClasses,
  layoutKind,
  getSavedFields: campaignFields,
  getHistorySummary: campaignHistorySummary,
});

export const REPORT_FAMILY_ADAPTERS = {
  search: campaignAdapter(
    "Search",
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    "campaign-original",
  ),
  onsite: campaignAdapter(
    "Display",
    "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    "onsite",
  ),
  offsite: campaignAdapter(
    "Offsite",
    "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    "offsite",
  ),
  social: {
    label: "Social",
    colorClasses:
      "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300",
    layoutKind: "social",
    getSavedFields: (data: SocialCampaignReportResponse) => ({
      date_range_start: data.summary.date_range_start,
      date_range_end: data.summary.date_range_end,
      total_spend: data.summary.total_spend,
    }),
    getHistorySummary: (report) => ({
      primaryValue: report.total_spend,
      primaryLabel: "spend",
      detailLeft: `${report.data.campaigns?.length || 0} campaigns`,
      detailRight: `${report.data.records_processed || 0} records`,
    }),
  } satisfies ReportFamilyAdapter<SocialCampaignReportResponse>,
  linear: {
    label: "Linear TV",
    colorClasses:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    layoutKind: "linear",
    getSavedFields: (data: LinearReportResponse) => ({
      date_range_start: data.summary.date_range_start,
      date_range_end: data.summary.date_range_end,
      total_spend: data.summary.total_spend,
      channel_count: data.summary.channel_count,
    }),
    getHistorySummary: (report) => ({
      primaryValue: report.total_spend,
      primaryLabel: "spend",
      detailLeft: `${report.channel_count || 0} channels`,
      detailRight: `${report.data.records_processed || 0} records`,
    }),
  } satisfies ReportFamilyAdapter<LinearReportResponse>,
  streaming: {
    label: "Streaming",
    colorClasses:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
    layoutKind: "streaming",
    getSavedFields: (data: StreamingReportResponse) => ({
      date_range_start: data.summary.date_range_start,
      date_range_end: data.summary.date_range_end,
      total_spend: data.summary.total_spend,
    }),
    getHistorySummary: (report) => ({
      primaryValue: report.total_spend,
      primaryLabel: "spend",
      detailLeft: `${report.data.networks?.length || 0} networks`,
      detailRight: `${report.data.records_processed || 0} records`,
    }),
  } satisfies ReportFamilyAdapter<StreamingReportResponse>,
  hess: {
    label: "Hess",
    colorClasses:
      "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
    layoutKind: "hess",
    getSavedFields: (data: HessReportResponse) => ({
      date_range_start: data.summary.date_range_start,
      date_range_end: data.summary.date_range_end,
      total_sales: data.summary.total_sales,
      total_spend: data.summary.total_ad_spend,
    }),
    getHistorySummary: (report) => ({
      primaryValue: report.total_sales,
      primaryLabel: "sales",
      detailLeft: `${report.data.products.length} SKUs`,
      detailRight: `${report.data.records_processed || 0} records`,
    }),
  } satisfies ReportFamilyAdapter<HessReportResponse>,
  comparison: {
    label: "Comparison",
    colorClasses:
      "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
    layoutKind: "comparison",
    getSavedFields: (data: ComparisonReportResponse) => ({
      date_range_start: data.current_summary.date_range_start,
      date_range_end: data.current_summary.date_range_end,
      total_sales: data.current_summary.total_sales,
      total_spend: data.current_summary.total_spend,
    }),
    getHistorySummary: (report) => ({
      primaryValue: report.total_sales,
      primaryLabel: "sales",
      detailLeft: "comparison",
      detailRight: `${report.data.records_processed || 0} records`,
    }),
  } satisfies ReportFamilyAdapter<ComparisonReportResponse>,
} satisfies Record<ReportType, ReportFamilyAdapter>;

export function getReportFamilyAdapter(type: ReportType): ReportFamilyAdapter {
  return REPORT_FAMILY_ADAPTERS[type] as ReportFamilyAdapter;
}

export function getSavedReportFields(data: ReportData): SavedReportSummaryFields {
  return getReportFamilyAdapter(data.report_type).getSavedFields(data);
}

export function getReportHistorySummary(
  report: SavedReportSummaryFields & { report_type: ReportType; data: ReportData },
): ReportHistorySummary {
  return getReportFamilyAdapter(report.report_type).getHistorySummary(report);
}

export function getReportTypeCounts(
  reports: Array<{ report_type: ReportType }>,
): Record<ReportType, number> {
  return Object.keys(REPORT_FAMILY_ADAPTERS).reduce(
    (counts, reportType) => ({
      ...counts,
      [reportType]: reports.filter((report) => report.report_type === reportType)
        .length,
    }),
    {} as Record<ReportType, number>,
  );
}

export function getReportLayoutKind(
  data: ReportData,
  reportProduct: string | null,
  viewMode: "original" | "slide" | "compact",
): ReportLayoutKind {
  if (
    reportProduct === "gyoza" &&
    "products" in data &&
    "lift_metrics" in data
  ) {
    return "gyoza";
  }

  const defaultLayoutKind = getReportFamilyAdapter(data.report_type).layoutKind;
  if (defaultLayoutKind !== "campaign-original") {
    return defaultLayoutKind;
  }

  if (viewMode === "slide") {
    return "campaign-slide";
  }
  if (viewMode === "compact") {
    return "campaign-compact";
  }
  return "campaign-original";
}
